import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Copy, Check, Mail, Loader2, Send, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

/**
 * Interactive pricing calculator — drag the slider, see live per-car rate
 * and monthly total. Mirrors the backend pricing tiers in `stripe_service.py`.
 *
 * Tiers:
 *   1–199 cars  → $1.00 / car
 *   200–499     → $0.90 / car (10% off)
 *   500–999     → $0.85 / car (15% off)
 *   1000+       → Request a quote (enterprise)
 */
const TIERS = [
  { min: 1, max: 199, rate: 1.0, label: "Tier 1", discount: null },
  { min: 200, max: 499, rate: 0.9, label: "Tier 2", discount: "10% off" },
  { min: 500, max: 999, rate: 0.85, label: "Tier 3", discount: "15% off" },
];
const QUOTE_THRESHOLD = 1000;
const SLIDER_MAX = 1000;
const PRESETS = [25, 100, 300, 750, 1000];

function tierFor(cars) {
  if (cars >= QUOTE_THRESHOLD) return null; // quote
  return TIERS.find((t) => cars >= t.min && cars <= t.max) || TIERS[0];
}

export default function PricingCalculator() {
  const [cars, setCars] = useState(50);
  const [copied, setCopied] = useState(false);
  const [leadEmail, setLeadEmail] = useState("");
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSentTo, setLeadSentTo] = useState("");

  const view = useMemo(() => {
    const isQuote = cars >= QUOTE_THRESHOLD;
    const tier = tierFor(cars);
    const monthly = isQuote || !tier ? 0 : cars * tier.rate;
    const tier1 = cars * TIERS[0].rate;
    const savings = isQuote ? 0 : Math.max(0, tier1 - monthly);
    return { isQuote, tier, monthly, savings };
  }, [cars]);

  const shareText = useMemo(() => {
    if (view.isQuote) {
      return (
        `1000+ cars on ScanMint — enterprise tier (custom quote).\n` +
        `$99 setup fee. First month free. No contract.`
      );
    }
    const total = view.monthly.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return (
      `${cars.toLocaleString()} cars at $${view.tier.rate.toFixed(2)}/car = $${total}/month on ScanMint.\n` +
      `$99 setup fee. First month free. No contract.`
    );
  }, [cars, view]);

  const copyEstimate = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareText);
      } else {
        // Legacy fallback for older browsers / non-https previews
        const ta = document.createElement("textarea");
        ta.value = shareText;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const emailHref = useMemo(() => {
    const subject = view.isQuote
      ? "ScanMint enterprise quote — 1000+ cars"
      : `My ScanMint estimate — ${cars.toLocaleString()} cars`;
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareText)}`;
  }, [cars, view.isQuote, shareText]);

  const submitLead = async (e) => {
    e.preventDefault();
    const email = leadEmail.trim();
    if (!email) {
      toast.error("Enter your email address");
      return;
    }
    setLeadSubmitting(true);
    try {
      await api.post("/leads/pricing-estimate", { email, cars });
      setLeadSentTo(email);
      setLeadEmail("");
      toast.success("Estimate sent — check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLeadSubmitting(false);
    }
  };

  const resetLeadForm = () => {
    setLeadSentTo("");
    setLeadEmail("");
  };

  return (
    <div
      className="mt-10 bg-white border border-[#E4E4E7] rounded-2xl p-6 md:p-10 shadow-sm"
      data-testid="pricing-calculator"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="mono-tiny text-[#0F8F4F] flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" strokeWidth={2} />
            Calculate Your Monthly Bill
          </div>
          <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold tracking-tight">
            How much will ScanMint cost?
          </h3>
        </div>
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0F8F4F]/10 text-[#0F8F4F] border border-[#0F8F4F]/30 text-xs font-semibold uppercase tracking-wider"
          data-testid="calc-free-badge"
        >
          First month FREE after $99 setup
        </div>
      </div>

      <p className="mt-3 text-sm text-[#71717A] max-w-2xl">
        Drag the slider or tap a preset to see your live monthly rate. Crossing
        into a discount lowers the price on <em>every</em> car, not just the new ones.
      </p>

      {/* Live readout */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          className="rounded-xl border border-[#E4E4E7] p-5 bg-[#FAFAFA]"
          data-testid="calc-cars-card"
        >
          <div className="mono-tiny text-[#71717A]">Active cars</div>
          <div className="mt-2 font-display text-3xl md:text-4xl font-bold leading-none">
            <span data-testid="calc-cars-value">
              {cars >= SLIDER_MAX ? "1,000+" : cars.toLocaleString()}
            </span>
          </div>
          <div className="mt-2 text-xs text-[#71717A]">
            {view.isQuote ? "Enterprise tier" : view.tier?.label}
          </div>
        </div>

        <div
          className="rounded-xl border border-[#E4E4E7] p-5 bg-[#FAFAFA]"
          data-testid="calc-rate-card"
        >
          <div className="mono-tiny text-[#71717A]">Per-car rate</div>
          <div
            className="mt-2 font-display text-3xl md:text-4xl font-bold leading-none text-[#0F8F4F]"
            data-testid="calc-rate-value"
          >
            {view.isQuote ? "Custom" : `$${view.tier.rate.toFixed(2)}`}
          </div>
          <div className="mt-2 text-xs text-[#71717A]">
            {view.isQuote
              ? "Talk to sales for volume pricing"
              : view.tier.discount
              ? `${view.tier.discount} vs Tier 1`
              : "Standard rate"}
          </div>
        </div>

        <div
          className={`rounded-xl border p-5 ${
            view.isQuote
              ? "border-[#5B21B6]/30 bg-[#5B21B6]/5"
              : "border-[#0F8F4F]/30 bg-[#0F8F4F]/5"
          }`}
          data-testid="calc-total-card"
        >
          <div className="mono-tiny text-[#71717A]">
            {view.isQuote ? "Estimate" : "Estimated monthly total"}
          </div>
          <div
            className="mt-2 font-display text-3xl md:text-4xl font-bold leading-none"
            data-testid="calc-total-value"
          >
            {view.isQuote
              ? "Request a quote"
              : `$${view.monthly.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
          </div>
          <div className="mt-2 text-xs text-[#71717A]">
            {view.isQuote ? (
              "1000+ cars — custom contract"
            ) : view.savings > 0 ? (
              <>
                Saving{" "}
                <b className="text-[#0F8F4F]">
                  ${view.savings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                </b>{" "}
                vs the standard rate
              </>
            ) : (
              "After 30-day free window"
            )}
          </div>
        </div>
      </div>

      {/* Share estimate */}
      <div
        className="mt-5 flex flex-wrap items-center gap-2"
        data-testid="calc-share-row"
      >
        <button
          type="button"
          onClick={copyEstimate}
          aria-live="polite"
          data-testid="calc-copy-estimate"
          className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold border transition-colors ${
            copied
              ? "bg-[#0F8F4F] text-white border-[#0F8F4F]"
              : "bg-white text-[#09090B] border-[#E4E4E7] hover:border-[#0F8F4F]/60 hover:text-[#0F8F4F]"
          }`}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" strokeWidth={2} /> Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" strokeWidth={2} /> Copy estimate
            </>
          )}
        </button>
        <a
          href={emailHref}
          data-testid="calc-email-estimate"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold border bg-white text-[#09090B] border-[#E4E4E7] hover:border-[#0F8F4F]/60 hover:text-[#0F8F4F] transition-colors"
        >
          <Mail className="w-4 h-4" strokeWidth={2} /> Email this estimate
        </a>
        <span
          className="text-xs text-[#71717A] sm:ml-1"
          data-testid="calc-share-hint"
          aria-hidden={!copied}
        >
          {copied
            ? "Estimate copied to your clipboard."
            : "Share this with your team or paste into an email."}
        </span>
      </div>

      {/* Slider */}
      <div className="mt-8">
        <div className="flex items-center justify-between text-xs text-[#71717A] mb-3">
          <span>1 car</span>
          <span>{SLIDER_MAX.toLocaleString()}+ cars</span>
        </div>
        <Slider
          value={[Math.min(cars, SLIDER_MAX)]}
          min={1}
          max={SLIDER_MAX}
          step={1}
          onValueChange={(v) => setCars(v[0])}
          className="w-full"
          aria-label="Number of active cars"
          data-testid="calc-slider"
        />
        {/* Tick markers — visual reference for tier boundaries */}
        <div className="relative mt-2 h-4 text-[10px] text-[#A1A1AA]">
          {[
            { v: 1, label: "1" },
            { v: 200, label: "200" },
            { v: 500, label: "500" },
            { v: 1000, label: "1000+" },
          ].map((t) => (
            <div
              key={t.v}
              className="absolute -translate-x-1/2"
              style={{ left: `${((t.v - 1) / (SLIDER_MAX - 1)) * 100}%` }}
            >
              <div className="w-px h-2 bg-[#D4D4D8] mx-auto" />
              <div className="mt-0.5">{t.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Preset chips */}
      <div className="mt-8 flex flex-wrap gap-2" data-testid="calc-presets">
        {PRESETS.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setCars(n)}
            data-testid={`calc-preset-${n}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              cars === n
                ? "bg-[#0F8F4F] text-white border-[#0F8F4F]"
                : "bg-white text-[#3F3F46] border-[#E4E4E7] hover:border-[#0F8F4F]/50 hover:text-[#0F8F4F]"
            }`}
          >
            {n >= QUOTE_THRESHOLD ? "1000+" : n} cars
          </button>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        {view.isQuote ? (
          <a
            href="mailto:support@scanmint.app?subject=ScanMint%20Enterprise%20Quote%20Request"
            data-testid="calc-cta-quote"
          >
            <Button className="bg-[#5B21B6] hover:bg-[#4C1D95] h-12 px-6 text-base text-white">
              Request a quote <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
        ) : (
          <Link to="/register" data-testid="calc-cta-start">
            <Button className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-12 px-6 text-base">
              Start for $99 <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        )}
        <div className="text-xs text-[#71717A] flex items-center sm:ml-3">
          $99 one-time setup · cancel anytime · ACH &amp; card via Stripe
        </div>
      </div>

      {/* Lead capture — "Email this estimate to me" */}
      <div
        className="mt-8 pt-8 border-t border-[#E4E4E7]"
        data-testid="calc-lead-capture"
      >
        {!leadSentTo ? (
          <div className="grid md:grid-cols-5 gap-6 items-start">
            <div className="md:col-span-2">
              <div className="mono-tiny text-[#0F8F4F]">Not ready yet?</div>
              <h4 className="mt-2 font-display text-xl md:text-2xl font-bold tracking-tight">
                Get this estimate emailed to you
              </h4>
              <p className="mt-2 text-sm text-[#71717A]">
                We'll send the full breakdown — no spam, just your estimate
                and a link to book a demo if you'd like one.
              </p>
            </div>
            <form
              onSubmit={submitLead}
              className="md:col-span-3 flex flex-col sm:flex-row gap-2"
              data-testid="calc-lead-form"
            >
              <Input
                type="email"
                placeholder="you@company.com"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                disabled={leadSubmitting}
                required
                autoComplete="email"
                className="h-12 flex-1"
                data-testid="calc-lead-email"
              />
              <Button
                type="submit"
                disabled={leadSubmitting}
                className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-12 px-5 text-sm shrink-0"
                data-testid="calc-lead-submit"
              >
                {leadSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Send me this estimate
                  </>
                )}
              </Button>
            </form>
          </div>
        ) : (
          <div
            className="rounded-xl border border-[#0F8F4F]/30 bg-[#0F8F4F]/5 p-5 md:p-6"
            data-testid="calc-lead-success"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#0F8F4F] text-white flex items-center justify-center shrink-0">
                <Check className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <div className="font-display text-lg md:text-xl font-bold">
                  Estimate sent to{" "}
                  <span
                    className="text-[#0F8F4F] break-all"
                    data-testid="calc-lead-success-email"
                  >
                    {leadSentTo}
                  </span>
                </div>
                <p className="mt-1 text-sm text-[#3F3F46]">
                  Check your inbox in the next minute. While you're here —
                  ready to set things up?
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <Link to="/register" data-testid="calc-lead-create-account">
                    <Button className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-11 px-5 text-sm">
                      Create account <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a
                    href="mailto:support@scanmint.app?subject=Book%20a%20ScanMint%20demo"
                    data-testid="calc-lead-book-demo"
                  >
                    <Button
                      variant="outline"
                      className="h-11 px-5 text-sm border-[#E4E4E7]"
                    >
                      <CalendarCheck className="w-4 h-4 mr-2" /> Book a demo
                    </Button>
                  </a>
                  <button
                    type="button"
                    onClick={resetLeadForm}
                    className="text-xs text-[#71717A] hover:text-[#0F8F4F] sm:ml-2 self-center"
                    data-testid="calc-lead-reset"
                  >
                    Send to a different email
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
